import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { MarkovState } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import {
    PRM_MARKOV_MODEL,
    StoreType,
    MarkovStateFilters,
    MarkovRedistributionStateFromList,
    MarkovRedistributionStateCalculated,

    LoadAllMarkovRedistributionStatesFrom,
    DeleteMarkovRedistributionStateFrom,
    SortMarkovStates,
    MarkovExpressionCalc,

    setMarkovRedistributionStateFromList,
    setMarkovRedistributionStateFromIds,
    setMarkovRedistributionStateFromCalculatedExpressions,
    setMarkovRedistributionStateFromSort,
    setMarkovRedistributionStateFromFilters,
    addModal,
} from "client/store"

import {
    Container,
    Loader,
    PageForm,
    FormGroup,
    Label,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import MarkovStateSearch from "client/pages/search/markovStateSearch"
import EditRedistributionState from "client/pages/markov/redistributionStateFrom/edit"
import Columns from "./columns"


const List = () => {
    const modelStateList = useSelector((state: StoreType) => state.markovState.stateList)
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const redistributionSelected = useSelector((state: StoreType) => state.markovRedistribution.redistributionSelected)

    const redistributionStateList = useSelector((state: StoreType) => state.markovRedistributionState.from.stateList)
    const stateIds = useSelector((state: StoreType) => state.markovRedistributionState.from.stateIds)
    const sortColumn = useSelector((state: StoreType) => state.markovRedistributionState.from.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovRedistributionState.from.sortOrder)
    const filters = useSelector((state: StoreType) => state.markovRedistributionState.from.filters)
    const reloadList = useSelector((state: StoreType) => state.markovRedistributionState.from.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllMarkovRedistributionStatesFrom(redistributionSelected && redistributionSelected.id).then((res) => {
            dispatch(setMarkovRedistributionStateFromList(res))
            setLoadState(IDLE)
        })
    }, [redistributionSelected, reloadList])

    useEffect(() => {
        if (
            !redistributionStateList || !Object.values(redistributionStateList).length
            || !variableList || !Object.values(variableList).length
        ) return
        const calculated: MarkovRedistributionStateCalculated = {}
        Object.values(redistributionStateList).forEach((redistributionState) => {
            if (!redistributionState.expression.length) return
            const calcResult = MarkovExpressionCalc(null, redistributionState.expression, variableList, lang)
            if (calcResult.error) {
                calculated[redistributionState.stateId] = calcResult.error
            } else {
                calculated[redistributionState.stateId] = calcResult.value
            }
        })
        dispatch(setMarkovRedistributionStateFromCalculatedExpressions(calculated))
    }, [redistributionStateList, variableList])

    useEffect(() => {
        const sortedIds = SortMarkovStates(Object.values(modelStateList), sortColumn, sortOrder, filters)
        const sortedIdsFiltered = sortedIds.filter((id) => !!redistributionStateList[id])
        dispatch(setMarkovRedistributionStateFromIds(sortedIdsFiltered))
    }, [modelStateList, redistributionStateList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MarkovState, order) => {
        dispatch(setMarkovRedistributionStateFromSort(column, order))
    }

    const onFilter = (newFilters: MarkovStateFilters) => {
        dispatch(setMarkovRedistributionStateFromFilters(newFilters))
    }

    const onDelete: DeleteRow = (state: MarkovState, callback) => {
        DeleteMarkovRedistributionStateFrom(state.id, redistributionSelected.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const redistributionStateListCopy: MarkovRedistributionStateFromList = redistributionStateList
                    ? deepCopy(redistributionStateList) : {}
                delete redistributionStateListCopy[state.id]
                dispatch(setMarkovRedistributionStateFromList(redistributionStateListCopy))
                dispatch(setMarkovRedistributionStateFromIds(stateIds.filter((id) => id !== state.id)))
            })
        })
    }

    const columns = Columns()
    const editModelAccess = hasAccess(PRM_MARKOV_MODEL, EDIT)

    return (
        <Container>
            {
                loadState === PENDING
                && <Loader />
            }
            <PageForm noPaddingTop noPaddingBottom>
                <FormGroup>
                    <Label>{lang.statesFrom}</Label>
                </FormGroup>
            </PageForm>
            <TableSearchWrapper>
                <MarkovStateSearch
                    id="redistributionStateList"
                    rightOffset={editModelAccess}
                    filters={filters}
                    onFilter={onFilter}
                />
                {
                    editModelAccess && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                key: "addRedistributionState",
                                content: <EditRedistributionState stateId={0} />,
                            }))}
                        >
                            {lang.add}
                        </Button>
                    )
                }
            </TableSearchWrapper>
            <Table
                columns={columns}
                dataList={modelStateList}
                dataIds={stateIds}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                rowSize="sm"
                showPagination
                scrollToTopOnPagination={false}
                onSort={onSort}
                onDelete={onDelete}
            />
        </Container>
    )
}
export default List
